import type {
  InsertEnterpriseRiskMutation,
  InsertEnterpriseRiskMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

// Maps the tRPC create response (the full enterprise_risk row, which carries
// the generated Id) to the GraphQL mutation shape so the flag-gated wrapper
// returns one type regardless of which path ran.
const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
}): InsertEnterpriseRiskMutation => ({
  insertChildEnterpriseRisk: {
    __typename: 'IdOutput',
    Id: trpcData.Id,
  },
});

/**
 * tRPC implementation of the insert-enterprise-risk mutation (RSP-6498). NOT
 * imported outside this directory — consumers use the flag-gated
 * `useInsertEnterpriseRisk` wrapper.
 *
 * The tRPC procedure input is a `Tier`-discriminated union (tier 1 forbids a
 * parent; tiers 2/3 require one), so the flat GraphQL variables are narrowed
 * into the matching union member before the call.
 */
export const useInsertEnterpriseRiskTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.enterpriseRisk.insert.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.enterpriseRisk.register.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.enterpriseRisk.getById.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.enterpriseRisk.getByTier.queryKey(),
          }),
        ]);
      },
    }),
  });

  return {
    insertEnterpriseRisk: async (
      variables: InsertEnterpriseRiskMutationVariables
    ): Promise<InsertEnterpriseRiskMutation> => {
      const base = {
        Title: variables.Title,
        Description: variables.Description ?? '',
        Treatment: variables.Treatment ?? null,
        CustomAttributeData: variables.CustomAttributeData ?? null,
      };

      if (variables.Tier === 1) {
        return mapTrpcResponseToGraphQL(
          await mutation.mutateAsync({ ...base, Tier: 1, ParentId: null })
        );
      }

      if (!variables.ParentId) {
        throw new Error(
          'A parent enterprise risk is required for tier 2 and tier 3 risks'
        );
      }

      if (variables.Tier === 2) {
        return mapTrpcResponseToGraphQL(
          await mutation.mutateAsync({
            ...base,
            Tier: 2,
            ParentId: variables.ParentId,
          })
        );
      }

      if (variables.Tier === 3) {
        return mapTrpcResponseToGraphQL(
          await mutation.mutateAsync({
            ...base,
            Tier: 3,
            ParentId: variables.ParentId,
          })
        );
      }

      throw new Error(`Unsupported enterprise risk tier: ${variables.Tier}`);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
